import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Printer, BarChart2, ListOrdered, Users, X } from 'lucide-react';
import { buildTableReportHTML, printPages } from './printBuilders';
import useScopedClasses from '../useScopedClasses';

// Modular class analytics — ONE compact panel instead of a pile of
// reports: pick a category (Grades / Class lists), then the options
// for that category appear. Everything previews on screen and prints
// through the same generic list layout (logo + stats + table).
// Head-of-school only (backend enforces the matrix endpoint).
//
// "Mark columns" are the selectable marks everywhere: the moyenne
// générale, any period ON ITS OWN, or any period · exam (exam entries
// only where the period actually has exams — direct periods ARE the
// mark). Résultats d'examen can show one subject or ALL subjects with
// the per-student moyenne and ranking; Par matière compares subjects
// across SEVERAL selected columns.

const fmt = (v) => (v === null || v === undefined) ? null : Math.round(v * 100) / 100;

// standard competition ranking: equal values share a rank (1,2,2,4)
const rankRows = (rows) => {
  let last = null, lastRank = 0;
  return rows.map((r, i) => {
    const rank = (last !== null && r.value === last) ? lastRank : i + 1;
    last = r.value; lastRank = rank;
    return { ...r, rank };
  });
};

const chip = (active, onClick, label, Icon) => (
  <button key={label} onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
      active ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}>
    {Icon && <Icon size={14} />} {label}
  </button>
);

const AnalyticsView = ({ apiCall, year, showError }) => {
  const { visibleClasses, className, setClassName } = useScopedClasses(apiCall, year);
  const [sections, setSections] = useState([]);
  const [sectionCode, setSectionCode] = useState('');
  const [category, setCategory] = useState('grades');      // grades | lists
  const [gradesView, setGradesView] = useState('ranking'); // ranking | exam | subjects
  const [matrix, setMatrix] = useState(null);
  const [roster, setRoster] = useState([]);
  const [subjectId, setSubjectId] = useState('all');       // 'all' | ClassSubjectID
  const [examColKey, setExamColKey] = useState('');        // mark column for Résultats
  const [sortBy, setSortBy] = useState('rank');            // rank | alpha
  const [compareKeys, setCompareKeys] = useState([]);      // mark columns for Par matière
  const [periodKey, setPeriodKey] = useState('overall');   // Classement: 'overall' | PeriodID
  const [rankFilter, setRankFilter] = useState('all');     // all | top10 | fail
  const [listCols, setListCols] = useState({ id: true, gender: true, dob: false });
  const [bySection, setBySection] = useState(true);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (!className) return;
    apiCall(`/academic/sections?academicYear=${year}&className=${encodeURIComponent(className)}`)
      .then(s => setSections(s.filter(x => x.IsActive))).catch(() => setSections([]));
    setSectionCode('');
  }, [apiCall, year, className]);

  const load = useCallback(async () => {
    if (!className) return;
    try {
      setLoading(true);
      let url = `/academic/reports/analytics/class-grades?academicYear=${year}&className=${encodeURIComponent(className)}`;
      if (sectionCode) url += `&sectionCode=${encodeURIComponent(sectionCode)}`;
      setMatrix(await apiCall(url));

      let rUrl = `/academic/roster?academicYear=${year}&className=${encodeURIComponent(className)}`;
      if (sectionCode) rUrl += `&section=${encodeURIComponent(sectionCode)}`;
      setRoster(await apiCall(rUrl));
    } catch (err) { showError(err.message); }
    finally { setLoading(false); }
  }, [apiCall, year, className, sectionCode, showError]);

  useEffect(() => { load(); }, [load]);

  // ---------- the selectable mark columns ----------
  const markColumns = useMemo(() => {
    if (!matrix) return [];
    const cols = [{ key: 'overall', label: 'Moyenne générale', type: 'overall' }];
    for (const p of matrix.periods) {
      cols.push({ key: `p${p.PeriodID}`, label: p.PeriodName, type: 'period', periodId: p.PeriodID });
      const pex = matrix.exams.filter(e => e.PeriodID === p.PeriodID);
      const direct = pex.length === 1 && !!pex[0].IsDirect;
      if (!direct) {
        for (const e of pex) {
          cols.push({ key: `e${e.ExamID}`, label: `${p.PeriodName} · ${e.ExamName}`, type: 'exam', periodId: p.PeriodID, examId: e.ExamID });
        }
      }
    }
    return cols;
  }, [matrix]);
  const colByKey = useCallback((key) => markColumns.find(c => c.key === key), [markColumns]);

  useEffect(() => {
    // default Résultats column = first period; comparison = the periods
    if (!markColumns.length) return;
    setExamColKey(prev => markColumns.some(c => c.key === prev) ? prev : (markColumns[1]?.key || markColumns[0].key));
    setCompareKeys(prev => {
      const kept = prev.filter(k => markColumns.some(c => c.key === k));
      return kept.length ? kept : markColumns.filter(c => c.type === 'period').map(c => c.key);
    });
    setSubjectId(prev => prev === 'all' || matrix?.subjects.some(s => String(s.ClassSubjectID) === String(prev)) ? prev : 'all');
  }, [markColumns, matrix]);

  // value of one subject for one student in one mark column
  const valueOf = (stu, csid, col) => {
    const ps = stu.perSubject[csid];
    if (!ps || !col) return null;
    let v;
    if (col.type === 'exam') v = ps.perExam?.[col.examId];
    else if (col.type === 'period') v = ps.perPeriod?.[col.periodId];
    else v = ps.general;
    return v === undefined ? null : v;
  };
  const exemptOf = (stu, csid, col) => {
    const ps = stu.perSubject[csid];
    if (!ps || !col) return false;
    if (col.type === 'exam') return !!ps.exemptExams?.[col.examId];
    if (col.type === 'period') return !!ps.perPeriodExempt?.[col.periodId];
    return !!ps.generalExempt;
  };
  // the student's moyenne /20 for a mark column (coef-weighted)
  const moyenneOf = (stu, col) => {
    if (!matrix || !col) return null;
    if (col.type === 'overall') return stu.moyennes.overall ?? null;
    if (col.type === 'period') return stu.moyennes[col.periodId] ?? null;
    let coefSum = 0, acc = 0;
    for (const sub of matrix.subjects) {
      const v = valueOf(stu, sub.ClassSubjectID, col);
      if (v !== null) { coefSum += sub.Coefficient; acc += (v / sub.MaxGrade) * 20 * sub.Coefficient; }
    }
    return coefSum > 0 ? fmt(acc / coefSum) : null;
  };

  const scopeLabel = `${className}${sectionCode ? ` ${sectionCode}` : ''} · ${year}`;
  const periodName = (key) => key === 'overall' ? 'Moyenne générale'
    : (matrix?.periods.find(p => String(p.PeriodID) === String(key))?.PeriodName || '');

  // ---------- derived tables ----------
  const subject = subjectId === 'all' ? null
    : matrix?.subjects.find(s => String(s.ClassSubjectID) === String(subjectId));
  const examCol = colByKey(examColKey);

  const sortRows = (ranked) => sortBy === 'alpha'
    ? [...ranked].sort((a, b) => a.name.localeCompare(b.name))
    : ranked;

  // Résultats — one subject
  const examTable = useMemo(() => {
    if (!matrix || !subject || !examCol) return null;
    const rows = matrix.students.map(s => {
      const v = valueOf(s, subject.ClassSubjectID, examCol);
      const d = v === null && exemptOf(s, subject.ClassSubjectID, examCol);
      return { name: `${s.LastName} ${s.FirstName}`, section: s.Section || '', value: v, display: v ?? (d ? 'D' : '—') };
    });
    const graded = rows.filter(r => r.value !== null);
    const ranked = rankRows([...graded].sort((a, b) => b.value - a.value));
    const rest = rows.filter(r => r.value === null).map(r => ({ ...r, rank: '' }));
    const avg = graded.length ? fmt(graded.reduce((s, r) => s + r.value, 0) / graded.length) : null;
    const pass = graded.filter(r => r.value >= subject.MaxGrade / 2).length;
    return {
      rows: [...sortRows(ranked), ...rest],
      stats: [
        { label: 'Présents', value: `${graded.length}/${rows.length}` },
        { label: 'Moyenne', value: avg !== null ? `${avg} / ${subject.MaxGrade}` : '—' },
        { label: 'Max', value: graded.length ? fmt(Math.max(...graded.map(r => r.value))) : '—' },
        { label: 'Min', value: graded.length ? fmt(Math.min(...graded.map(r => r.value))) : '—' },
        { label: 'Réussite', value: graded.length ? `${Math.round(pass / graded.length * 100)}%` : '—' }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix, subject, examCol, sortBy]);

  // Résultats — ALL subjects: one column per subject + moyenne + rank
  const allTable = useMemo(() => {
    if (!matrix || subjectId !== 'all' || !examCol) return null;
    const rows = matrix.students.map(s => {
      const per = {};
      for (const sub of matrix.subjects) {
        const v = valueOf(s, sub.ClassSubjectID, examCol);
        per[sub.ClassSubjectID] = v ?? (exemptOf(s, sub.ClassSubjectID, examCol) ? 'D' : '');
      }
      const moy = moyenneOf(s, examCol);
      return { name: `${s.LastName} ${s.FirstName}`, section: s.Section || '', per, value: moy, display: moy ?? '—' };
    });
    const graded = rows.filter(r => r.value !== null);
    const ranked = rankRows([...graded].sort((a, b) => b.value - a.value));
    const rest = rows.filter(r => r.value === null).map(r => ({ ...r, rank: '' }));
    const avg = graded.length ? fmt(graded.reduce((s, r) => s + r.value, 0) / graded.length) : null;
    return {
      rows: [...sortRows(ranked), ...rest],
      stats: [
        { label: 'Notés', value: `${graded.length}/${rows.length}` },
        { label: 'Moyenne de classe', value: avg !== null ? `${avg} / 20` : '—' },
        { label: 'Réussite', value: graded.length ? `${Math.round(graded.filter(r => r.value >= 10).length / graded.length * 100)}%` : '—' }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix, subjectId, examCol, sortBy]);

  // Classement
  const rankingTable = useMemo(() => {
    if (!matrix) return null;
    const rows = matrix.students.map(s => {
      const v = periodKey === 'overall' ? s.moyennes.overall : s.moyennes[periodKey];
      return { name: `${s.LastName} ${s.FirstName}`, section: s.Section || '', value: v ?? null, display: v ?? '—', res: v === null || v === undefined ? '' : (v >= 10 ? 'R' : 'E') };
    });
    const graded = rankRows(rows.filter(r => r.value !== null).sort((a, b) => b.value - a.value));
    const rest = rows.filter(r => r.value === null).map(r => ({ ...r, rank: '' }));
    let shown = [...graded, ...rest];
    if (rankFilter === 'top10') shown = graded.slice(0, 10);
    if (rankFilter === 'fail') shown = graded.filter(r => r.value < 10);
    const avg = graded.length ? fmt(graded.reduce((s, r) => s + r.value, 0) / graded.length) : null;
    return {
      rows: shown,
      stats: [
        { label: 'Notés', value: `${graded.length}/${rows.length}` },
        { label: 'Moyenne de classe', value: avg !== null ? `${avg} / 20` : '—' },
        { label: 'Max', value: graded.length ? graded[0].display : '—' },
        { label: 'Min', value: graded.length ? graded[graded.length - 1].display : '—' },
        { label: 'Réussite', value: graded.length ? `${Math.round(graded.filter(r => r.value >= 10).length / graded.length * 100)}%` : '—' }
      ]
    };
  }, [matrix, periodKey, rankFilter]);

  // Par matière — subjects across SEVERAL mark columns
  const compareCols = compareKeys.map(colByKey).filter(Boolean);
  const subjectsTable = useMemo(() => {
    if (!matrix || !compareCols.length) return null;
    const single = compareCols.length === 1;
    const rows = matrix.subjects.map(sub => {
      const r = { name: sub.SubjectName, max: sub.MaxGrade };
      for (const col of compareCols) {
        const vals = matrix.students
          .map(s => valueOf(s, sub.ClassSubjectID, col))
          .filter(v => v !== null);
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
        r[`avg_${col.key}`] = avg !== null ? fmt(avg) : '—';
        r[`s20_${col.key}`] = avg !== null ? fmt(avg / sub.MaxGrade * 20) : '—';
        r[`pass_${col.key}`] = vals.length ? `${Math.round(vals.filter(v => v >= sub.MaxGrade / 2).length / vals.length * 100)}%` : '—';
        if (single) {
          r.n = vals.length;
          r.min = vals.length ? fmt(Math.min(...vals)) : '—';
          r.maxv = vals.length ? fmt(Math.max(...vals)) : '—';
        }
      }
      return r;
    });
    return { rows, single };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix, compareKeys]);

  // ---------- printing ----------
  const printGrades = () => {
    if (gradesView === 'exam' && subjectId === 'all' && allTable && examCol) {
      const columns = [
        { key: 'rank', label: '#', align: 'center' }, { key: 'name', label: 'Élève' },
        { key: 'section', label: 'Sec.', align: 'center' },
        ...matrix.subjects.map(sub => ({ key: `sub_${sub.ClassSubjectID}`, label: `${sub.SubjectName} /${sub.MaxGrade}`, align: 'center' })),
        { key: 'display', label: 'Moy. /20', align: 'center' }
      ];
      const rows = allTable.rows.map(r => {
        const o = { rank: r.rank, name: r.name, section: r.section, display: r.display };
        for (const sub of matrix.subjects) o[`sub_${sub.ClassSubjectID}`] = r.per[sub.ClassSubjectID];
        return o;
      });
      printPages(`Résultats — ${scopeLabel}`, [buildTableReportHTML({
        title: `Résultats — Toutes les matières`,
        subtitle: `${examCol.label} · ${scopeLabel}`,
        stats: allTable.stats, columns, rows
      })], 'landscape');
    } else if (gradesView === 'exam' && examTable && subject && examCol) {
      printPages(`Résultats — ${subject.SubjectName}`, [buildTableReportHTML({
        title: `Résultats — ${subject.SubjectName}`,
        subtitle: `${examCol.label} · ${scopeLabel}`,
        stats: examTable.stats,
        columns: [{ key: 'rank', label: '#', align: 'center' }, { key: 'name', label: 'Élève' },
                  { key: 'section', label: 'Section', align: 'center' },
                  { key: 'display', label: `Note / ${subject.MaxGrade}`, align: 'center' }],
        rows: examTable.rows
      })]);
    } else if (gradesView === 'ranking' && rankingTable) {
      printPages(`Classement — ${scopeLabel}`, [buildTableReportHTML({
        title: `Classement — ${periodName(periodKey)}`,
        subtitle: `${scopeLabel}${rankFilter === 'top10' ? ' · Top 10' : rankFilter === 'fail' ? ' · En difficulté (< 10)' : ''}`,
        stats: rankingTable.stats,
        columns: [{ key: 'rank', label: '#', align: 'center' }, { key: 'name', label: 'Élève' },
                  { key: 'section', label: 'Section', align: 'center' },
                  { key: 'display', label: 'Moyenne / 20', align: 'center' },
                  { key: 'res', label: 'R/E', align: 'center' }],
        rows: rankingTable.rows
      })]);
    } else if (gradesView === 'subjects' && subjectsTable) {
      const columns = [{ key: 'name', label: 'Matière' }, { key: 'max', label: 'Sur', align: 'center' }];
      for (const col of compareCols) {
        columns.push({ key: `avg_${col.key}`, label: `${col.label} — Moy`, align: 'center' });
        columns.push({ key: `s20_${col.key}`, label: '/20', align: 'center' });
        columns.push({ key: `pass_${col.key}`, label: 'Réuss.', align: 'center' });
      }
      if (subjectsTable.single) {
        columns.push({ key: 'min', label: 'Min', align: 'center' }, { key: 'maxv', label: 'Max', align: 'center' });
      }
      printPages(`Par matière — ${scopeLabel}`, [buildTableReportHTML({
        title: 'Comparaison par matière',
        subtitle: `${compareCols.map(c => c.label).join(' · ')} — ${scopeLabel}`,
        stats: [], columns, rows: subjectsTable.rows
      })], compareCols.length > 2 ? 'landscape' : 'portrait');
    }
  };

  const listColumns = useMemo(() => {
    const cols = [{ key: 'i', label: '#', align: 'center' }, { key: 'name', label: 'Élève' }];
    if (listCols.id) cols.push({ key: 'id', label: 'N°', align: 'center' });
    if (listCols.gender) cols.push({ key: 'gender', label: 'Sexe', align: 'center' });
    if (listCols.dob) cols.push({ key: 'dob', label: 'Naissance', align: 'center' });
    if (!bySection) cols.push({ key: 'section', label: 'Section', align: 'center' });
    return cols;
  }, [listCols, bySection]);

  const listRow = (s, i) => ({
    i: i + 1, name: `${s.LastName} ${s.FirstName}`, id: s.StudentID,
    gender: s.Gender || '', dob: s.DateOfBirth ? new Date(s.DateOfBirth).toLocaleDateString('fr-FR') : '',
    section: s.Section || ''
  });

  const printList = () => {
    if (bySection) {
      const codes = [...new Set(roster.map(s => s.Section || '—'))];
      const pages = codes.map(code => {
        const stus = roster.filter(s => (s.Section || '—') === code);
        return buildTableReportHTML({
          title: `Liste de classe — ${className} ${code}`,
          subtitle: `${year} · ${stus.length} élèves`,
          stats: [], columns: listColumns, rows: stus.map(listRow)
        });
      });
      printPages(`Listes ${className}`, pages);
    } else {
      printPages(`Liste ${className}`, [buildTableReportHTML({
        title: `Liste de classe — ${scopeLabel}`,
        subtitle: `${roster.length} élèves`,
        stats: [], columns: listColumns, rows: roster.map(listRow)
      })]);
    }
  };

  // ---------- render ----------
  const activeTable = gradesView === 'exam' ? (subjectId === 'all' ? allTable : examTable)
    : gradesView === 'ranking' ? rankingTable : subjectsTable;

  return (
    <div className="space-y-4">
      {/* One compact control card */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {chip(category === 'grades', () => setCategory('grades'), 'Grades', BarChart2)}
          {chip(category === 'lists', () => setCategory('lists'), 'Class Lists', Users)}
          <div className="w-px h-6 bg-gray-200 mx-1" />
          <select value={className} onChange={e => setClassName(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm">
            {visibleClasses.length === 0 && <option value="">No classes</option>}
            {visibleClasses.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
          </select>
          <select value={sectionCode} onChange={e => setSectionCode(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm">
            <option value="">All sections</option>
            {sections.map(s => <option key={s.SectionID} value={s.SectionCode}>{className} {s.SectionCode}</option>)}
          </select>
          <div className="flex-1" />
          <button onClick={category === 'grades' ? printGrades : printList}
            disabled={loading || (category === 'grades' ? !activeTable?.rows?.length : !roster.length)}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5">
            <Printer size={14} /> Print
          </button>
        </div>

        {category === 'grades' && (
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t">
            {chip(gradesView === 'ranking', () => setGradesView('ranking'), 'Classement', ListOrdered)}
            {chip(gradesView === 'exam', () => setGradesView('exam'), "Résultats d'examen")}
            {chip(gradesView === 'subjects', () => setGradesView('subjects'), 'Par matière')}
            <div className="w-px h-6 bg-gray-200 mx-1" />

            {gradesView === 'exam' && (
              <>
                <select value={subjectId} onChange={e => setSubjectId(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
                  <option value="all">Toutes les matières</option>
                  {matrix?.subjects.map(s => <option key={s.ClassSubjectID} value={s.ClassSubjectID}>{s.SubjectName}</option>)}
                </select>
                <select value={examColKey} onChange={e => setExamColKey(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
                  {markColumns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
                  <option value="rank">Tri: Classement</option>
                  <option value="alpha">Tri: Alphabétique</option>
                </select>
              </>
            )}

            {gradesView === 'ranking' && (
              <>
                <select value={periodKey} onChange={e => setPeriodKey(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
                  <option value="overall">Moyenne générale</option>
                  {matrix?.periods.map(p => <option key={p.PeriodID} value={p.PeriodID}>{p.PeriodName}</option>)}
                </select>
                <select value={rankFilter} onChange={e => setRankFilter(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
                  <option value="all">Tous</option>
                  <option value="top10">Top 10 (tableau d'honneur)</option>
                  <option value="fail">En difficulté (&lt; 10)</option>
                </select>
              </>
            )}

            {gradesView === 'subjects' && (
              <>
                {compareCols.map(col => (
                  <span key={col.key} className="flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 rounded-full px-2.5 py-1 text-xs">
                    {col.label}
                    <button onClick={() => setCompareKeys(ks => ks.filter(k => k !== col.key))}
                      className="hover:text-red-600"><X size={11} /></button>
                  </span>
                ))}
                <select value="" onChange={e => { const k = e.target.value; if (k) setCompareKeys(ks => ks.includes(k) ? ks : [...ks, k]); }}
                  className="border rounded-lg px-2 py-1.5 text-sm">
                  <option value="">+ Ajouter une colonne…</option>
                  {markColumns.filter(c => !compareKeys.includes(c.key)).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </>
            )}
          </div>
        )}

        {category === 'lists' && (
          <div className="flex flex-wrap items-center gap-4 pt-1 border-t text-sm text-gray-700">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={listCols.id}
              onChange={e => setListCols(c => ({ ...c, id: e.target.checked }))} /> N°</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={listCols.gender}
              onChange={e => setListCols(c => ({ ...c, gender: e.target.checked }))} /> Sexe</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={listCols.dob}
              onChange={e => setListCols(c => ({ ...c, dob: e.target.checked }))} /> Date de naissance</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={bySection}
              onChange={e => setBySection(e.target.checked)} /> Une page par section</label>
          </div>
        )}
      </div>

      {/* Stats + preview */}
      {loading ? (
        <p className="text-center text-gray-500 py-8">Loading…</p>
      ) : category === 'grades' ? (
        <>
          {activeTable?.stats && (
            <div className="flex flex-wrap gap-2">
              {activeTable.stats.map(s => (
                <span key={s.label} className="bg-white border rounded-lg px-3 py-1.5 text-sm shadow-sm">
                  <span className="text-gray-500">{s.label}:</span> <b>{s.value}</b>
                </span>
              ))}
            </div>
          )}
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {gradesView === 'subjects' ? (
                    <>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Matière</th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Sur</th>
                      {compareCols.map(col => (
                        <React.Fragment key={col.key}>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">{col.label}</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">/20</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Réuss.</th>
                        </React.Fragment>
                      ))}
                      {subjectsTable?.single && (
                        <>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Min</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Max</th>
                        </>
                      )}
                    </>
                  ) : gradesView === 'exam' && subjectId === 'all' ? (
                    <>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase w-10">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Élève</th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Sec.</th>
                      {matrix?.subjects.map(sub => (
                        <th key={sub.ClassSubjectID} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase"
                            title={sub.SubjectName}>
                          {sub.SubjectName.length > 10 ? `${sub.SubjectName.slice(0, 9)}…` : sub.SubjectName}
                          <span className="block font-normal">/{sub.MaxGrade}</span>
                        </th>
                      ))}
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Moy. /20</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-10">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Élève</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Section</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                        {gradesView === 'exam' ? `Note /${subject?.MaxGrade ?? ''}` : 'Moyenne /20'}
                      </th>
                      {gradesView === 'ranking' && <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">R/E</th>}
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {gradesView === 'subjects'
                  ? subjectsTable?.rows.map(r => (
                    <tr key={r.name} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-medium text-gray-800">{r.name}</td>
                      <td className="px-2 py-1.5 text-center text-gray-500">{r.max}</td>
                      {compareCols.map(col => (
                        <React.Fragment key={col.key}>
                          <td className="px-2 py-1.5 text-center border-l">{r[`avg_${col.key}`]}</td>
                          <td className="px-2 py-1.5 text-center font-semibold">{r[`s20_${col.key}`]}</td>
                          <td className="px-2 py-1.5 text-center text-gray-500">{r[`pass_${col.key}`]}</td>
                        </React.Fragment>
                      ))}
                      {subjectsTable.single && (
                        <>
                          <td className="px-2 py-1.5 text-center text-gray-500 border-l">{r.min}</td>
                          <td className="px-2 py-1.5 text-center text-gray-500">{r.maxv}</td>
                        </>
                      )}
                    </tr>
                  ))
                  : gradesView === 'exam' && subjectId === 'all'
                  ? allTable?.rows.map((r, i) => (
                    <tr key={`${r.name}-${i}`} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-center text-gray-500">{r.rank}</td>
                      <td className="px-3 py-1.5 font-medium text-gray-800 whitespace-nowrap">{r.name}</td>
                      <td className="px-2 py-1.5 text-center text-gray-400">{r.section}</td>
                      {matrix?.subjects.map(sub => (
                        <td key={sub.ClassSubjectID} className="px-2 py-1.5 text-center">{r.per[sub.ClassSubjectID]}</td>
                      ))}
                      <td className={`px-2 py-1.5 text-center font-semibold ${r.value !== null && r.value < 10 ? 'text-red-600' : ''}`}>{r.display}</td>
                    </tr>
                  ))
                  : activeTable?.rows.map((r, i) => (
                    <tr key={`${r.name}-${i}`} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 text-center text-gray-500">{r.rank}</td>
                      <td className="px-3 py-1.5 font-medium text-gray-800">{r.name}</td>
                      <td className="px-3 py-1.5 text-center text-gray-400">{r.section}</td>
                      <td className={`px-3 py-1.5 text-center font-semibold ${
                        r.value !== null && ((gradesView === 'exam' && subject && r.value < subject.MaxGrade / 2) || (gradesView === 'ranking' && r.value < 10))
                          ? 'text-red-600' : ''}`}>{r.display}</td>
                      {gradesView === 'ranking' && <td className="px-3 py-1.5 text-center">{r.res}</td>}
                    </tr>
                  ))}
                {(!activeTable || !activeTable.rows.length) && (
                  <tr><td colSpan="12" className="text-center text-gray-400 py-6">No data.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{listColumns.map(c => (
                <th key={c.key} className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase ${c.align === 'center' ? 'text-center' : 'text-left'}`}>{c.label}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {roster.map((s, i) => {
                const r = listRow(s, i);
                return (
                  <tr key={s.StudentID} className="hover:bg-gray-50">
                    {listColumns.map(c => (
                      <td key={c.key} className={`px-3 py-1.5 ${c.align === 'center' ? 'text-center text-gray-500' : 'font-medium text-gray-800'}`}>{r[c.key]}</td>
                    ))}
                  </tr>
                );
              })}
              {!roster.length && <tr><td colSpan={listColumns.length} className="text-center text-gray-400 py-6">No students.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnalyticsView;
